import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CoverageDisplayEnum } from '@app/core/enums/coverage-display.enum';
import {
    CoverageMergeOptionsInterface,
    CoverageMergePriceOptionsInterface,
    CoverageRemoveOptionsInterface,
} from '@app/core/interfaces';
import { CoverageTransformBuilder } from '@app/my-zone/builders/coverage-transform.builder';
import { coverageMergeOptionsConfig } from '@app/my-zone/config/coverage-merge-options.config';
import { coverageMergePriceOptionsConfig } from '@app/my-zone/config/coverage-merge-price-options.config';
import { coverageOrderOptionsConfig } from '@app/my-zone/config/coverage-order-options.config';
import { coverageRemoveOptionsConfig } from '@app/my-zone/config/coverage-remove-options.config';
import { CoveragePremiumTotalInterface } from '@app/my-zone/interfaces/coverage-premium-total.interface';
import { CoveragePriceService } from '@app/my-zone/services';
import { CoverageSpecificationInterface, PurchasedProductInterface } from 'outshared-lib';
import { CommonModule } from '@angular/common';
import { DateFormatPipeModule } from '@app/shared/pipes/date-format/date-format.module';
import {
    WucDisplayTableComponent, WucDisplayTableLabelComponent,
    WucDisplayTableRowComponent,
    WucDisplayTableValueComponent, WucPriceModule,
    WucTabsetItemModule,
    WucTabsetModule,
} from '@inshared/website-ui-components';

@Component({
    selector: 'ins-insurance-coverage-data-list',
    templateUrl: 'insurance-coverage-data-list.component.html',
    standalone: true,
    imports: [
        CommonModule,
        DateFormatPipeModule,
        WucTabsetModule,
        WucTabsetItemModule,
        WucDisplayTableValueComponent,
        WucDisplayTableRowComponent,
        WucDisplayTableComponent,
        WucDisplayTableLabelComponent,
        WucPriceModule,
    ]
})
export class InsuranceCoverageDataListComponent implements OnChanges, OnInit {
    @Input() public purchasedProduct!: PurchasedProductInterface;
    @Input() public display!: CoverageDisplayEnum;

    public coverages: CoverageSpecificationInterface[];
    public coveragePremiumTotal: CoveragePremiumTotalInterface;

    constructor(private coveragePriceService: CoveragePriceService) {
        this.coverages = [];
    }

    public ngOnInit(): void {
        this.setPurchasedProduct(this.purchasedProduct);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.purchasedProduct) {
            this.setPurchasedProduct(changes.purchasedProduct.currentValue);
        }
    }

    private setPurchasedProduct(purchasedProduct: PurchasedProductInterface): void {
        this.setCoverages(purchasedProduct);
        this.setCoveragePremiumTotal(purchasedProduct);
    }

    private setCoverages(purchasedProduct: PurchasedProductInterface): void {
        this.coverages = this.createCoverageList(purchasedProduct, this.display);
    }

    private setCoveragePremiumTotal(purchasedProduct: PurchasedProductInterface): void {
        this.coveragePremiumTotal = this.coveragePriceService.calculateCoveragePremiumTotal(
            purchasedProduct,
            this.display
        );
    }

    private createCoverageList(
        purchasedProduct: PurchasedProductInterface,
        display: CoverageDisplayEnum
    ): CoverageSpecificationInterface[] {
        const { productId, coverage } = purchasedProduct;
        const toRemove: CoverageRemoveOptionsInterface = coverageRemoveOptionsConfig[productId];
        const toMerge: CoverageMergeOptionsInterface = coverageMergeOptionsConfig[productId];
        const toMergePrice: CoverageMergePriceOptionsInterface[] = coverageMergePriceOptionsConfig[productId];
        const order: { [id: string]: number } = coverageOrderOptionsConfig[productId];

        const coverages = new CoverageTransformBuilder([...coverage.baseCoverages, ...coverage.additionalCoverages])
            .filterByDisplay(display)
            .filterUnnecessary()
            .mergePrice(toMergePrice)
            .remove(toRemove)
            .merge(toMerge)
            .order(order)
            .build();

        return coverages;
    }
}
